import { useEffect, useMemo, useRef, useSyncExternalStore } from "react"
import type {
  CollectionCompleteness,
  CollectionSource,
  CollectionSorting,
  CompletableCollection,
  QueryableCollection,
} from "./collection-source"

export interface LocalCollectionOptions<TItem, TQuery = void, TSortField extends string = string> {
  items: readonly TItem[]
  initialQuery: TQuery
  getKey: (item: TItem) => string | number
  filter?: (item: TItem, query: Readonly<TQuery>) => boolean
  /** Equivalent queries keep the current result set. Default: shallow equality of plain objects. */
  isQueryEqual?: (left: Readonly<TQuery>, right: Readonly<TQuery>) => boolean
  compare?: (left: TItem, right: TItem, sorting: CollectionSorting<TSortField>) => number
  sortableFields?: readonly TSortField[]
  pageSize?: number
}

export interface LocalCollection<TItem, TQuery = void, TSortField extends string = string>
  extends CollectionSource<TItem, TSortField>, QueryableCollection<TQuery>, CompletableCollection<TItem> {
  getLoadedSelectedItems(): readonly TItem[]
  /** Its filtered, sorted records; it always holds all of them. */
  getAllItems(): readonly TItem[]
  /** Replace the records in place; the current query and ordering are reapplied. */
  setItems(items: readonly TItem[]): void
  /** Reapply the query and ordering in place, e.g. after records were mutated. */
  refresh(): void
  /** Clear selection and start a new result set. */
  reset(): void
  dispose(): void
}

/** The hook owns the records and disposal. */
export type LocalCollectionHandle<TItem, TQuery = void, TSortField extends string = string> = Omit<
  LocalCollection<TItem, TQuery, TSortField>,
  "setItems" | "dispose"
>

function shallowEqual(left: unknown, right: unknown) {
  if (Object.is(left, right)) return true
  if (typeof left !== "object" || typeof right !== "object" || left === null || right === null) return false
  if (Array.isArray(left) !== Array.isArray(right)) return false
  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false
  } else {
    // Dates, maps, sets and class instances need identity or a supplied comparator.
    const leftPrototype = Object.getPrototypeOf(left)
    const rightPrototype = Object.getPrototypeOf(right)
    if (
      (leftPrototype !== Object.prototype && leftPrototype !== null) ||
      (rightPrototype !== Object.prototype && rightPrototype !== null)
    )
      return false
  }
  const leftKeys = Object.keys(left)
  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        Object.is((left as Record<string, unknown>)[key], (right as Record<string, unknown>)[key])
    )
  )
}

const COMPLETE: CollectionCompleteness = Object.freeze({ state: "complete" })
const releaseNothing = () => {}

function sortingEqual(left: CollectionSorting, right: CollectionSorting) {
  return (
    left.length === right.length &&
    left.every((sort, index) => sort.id === right[index].id && sort.desc === right[index].desc)
  )
}

/** Complete in-memory source; filtering and ordering happen behind the same consumer contract. */
export function createLocalCollection<TItem, TQuery = void, TSortField extends string = string>(
  options: LocalCollectionOptions<TItem, TQuery, TSortField>
): LocalCollection<TItem, TQuery, TSortField> {
  let allItems = options.items
  let query = options.initialQuery
  let sorting: CollectionSorting<TSortField> = []
  let selectedKeys: readonly string[] = []
  let items: readonly TItem[] = []
  let revision = 0
  let dataRevision = 0
  let resultSetRevision = 0
  const listeners = new Set<() => void>()
  const dataListeners = new Set<() => void>()
  const capabilities = Object.freeze({
    sorting: Object.freeze(options.compare ? [...(options.sortableFields ?? [])] : []),
    maxSortFields: 1,
  })
  const isQueryEqual = options.isQueryEqual ?? shallowEqual
  const getKey = (item: TItem) => String(options.getKey(item))
  const notify = (dataChanged: boolean) => {
    revision++
    if (dataChanged) dataRevision++
    for (const listener of listeners) listener()
    if (dataChanged) for (const listener of dataListeners) listener()
  }
  const select = () => {
    const next = allItems.filter((item) => options.filter?.(item, query) ?? true)
    if (sorting.length && options.compare) next.sort((a, b) => options.compare!(a, b, sorting))
    return next
  }
  /** A new result set renumbers rows; an in-place update keeps the consumer's position. */
  const recompute = (newResultSet: boolean) => {
    items = select()
    if (newResultSet) resultSetRevision++
    notify(true)
  }
  items = select()
  const subscribeTo = (target: Set<() => void>) => (listener: () => void) => {
    target.add(listener)
    return () => {
      target.delete(listener)
    }
  }
  const source: LocalCollection<TItem, TQuery, TSortField> = {
    get query() {
      return query as Readonly<TQuery>
    },
    get sorting() {
      return sorting
    },
    get selectedKeys() {
      return selectedKeys
    },
    capabilities,
    pageSize: Math.max(1, Number.isFinite(options.pageSize) ? Math.floor(options.pageSize!) : 25),
    status: "ready",
    error: undefined,
    get revision() {
      return revision
    },
    get dataRevision() {
      return dataRevision
    },
    get resultSetRevision() {
      return resultSetRevision
    },
    get itemCount() {
      return items.length
    },
    get totalCount() {
      return items.length
    },
    get loadedCount() {
      return items.length
    },
    isLoading: false,
    // Every record is in memory, so the collection is always complete.
    completeness: COMPLETE,
    requireComplete: () => releaseNothing,
    getAllItems: () => items,
    get: (index) => (Number.isInteger(index) && index >= 0 ? items[index] : undefined),
    getLoadedEntries: () => items.entries(),
    getLoadedSelectedItems: () => {
      const selected = new Set(selectedKeys)
      return allItems.filter((item) => selected.has(getKey(item)))
    },
    getKey,
    getItemState: (index) => (Number.isInteger(index) && index >= 0 && index < items.length ? "loaded" : "unloaded"),
    getItemError: () => undefined,
    getItemErrorRange: () => undefined,
    applyQuery(next) {
      const resolved = typeof next === "function" ? (next as (previous: Readonly<TQuery>) => TQuery)(query) : next
      if (isQueryEqual(query, resolved)) return
      query = resolved
      selectedKeys = []
      recompute(true)
    },
    setSorting(next) {
      if (next.length > capabilities.maxSortFields || next.some((sort) => !capabilities.sorting.includes(sort.id)))
        throw new Error("Unsupported collection sorting.")
      if (sortingEqual(sorting, next)) return
      sorting = next.map((sort) => ({ id: sort.id, desc: sort.desc }))
      recompute(true)
    },
    setSelectedKeys(keys) {
      const next = [...new Set(keys)]
      if (next.length === selectedKeys.length && next.every((key, index) => key === selectedKeys[index])) return
      selectedKeys = next
      notify(false)
    },
    ensureRange: () => {},
    retryRange: async () => {},
    refresh: () => recompute(false),
    reset: () => {
      selectedKeys = []
      recompute(true)
    },
    subscribe: subscribeTo(listeners),
    subscribeData: subscribeTo(dataListeners),
    setItems(next) {
      if (allItems === next) return
      // Derived arrays may be recreated on every render. Notify only when
      // their records or ordering change, otherwise the owning hook loops.
      if (allItems.length === next.length && next.every((item, index) => Object.is(item, allItems[index]))) return
      allItems = next
      recompute(false)
    },
    dispose() {
      listeners.clear()
      dataListeners.clear()
    },
  }
  return source
}

/** Own a local source without making its consumers know where its records live. */
export function useLocalCollection<TItem, TQuery = void, TSortField extends string = string>(
  options: LocalCollectionOptions<TItem, TQuery, TSortField>
): LocalCollectionHandle<TItem, TQuery, TSortField> {
  const callbacks = useRef(options)
  callbacks.current = options
  const source = useMemo(
    () =>
      createLocalCollection({
        ...options,
        getKey: (item: TItem) => callbacks.current.getKey(item),
        filter: (item: TItem, query: Readonly<TQuery>) => callbacks.current.filter?.(item, query) ?? true,
        isQueryEqual: (left: Readonly<TQuery>, right: Readonly<TQuery>) =>
          (callbacks.current.isQueryEqual ?? shallowEqual)(left, right),
        compare: options.compare
          ? (a: TItem, b: TItem, sorting: CollectionSorting<TSortField>) => callbacks.current.compare!(a, b, sorting)
          : undefined,
      }),
    // Creation configuration is fixed for the source's lifetime; callbacks read the latest options.
    []
  )
  useEffect(() => {
    source.setItems(options.items)
  }, [source, options.items])
  useEffect(() => () => source.dispose(), [source])
  useSyncExternalStore(
    source.subscribe,
    () => source.revision,
    () => source.revision
  )
  return source
}
